import { useMemo } from "react";
import CustomLabel from "../labels/tinyLabels/CustomLabel";
import useTheme from "../../hooks/ui/useTheme";
import {useSelector} from "react-redux";
import classnames from "classnames";
import InfoIcon from "../../assets/logo/InfoIcon";
import PicturesSlider from "./PicturesSlider";
import { useTranslation } from "react-i18next";
import BackIcon from "../../assets/logo/BackIcon";
import {StoreInterface} from "../../store/types";
import useScreenType from "../../hooks/utility/useScreenType";
import {useNavigate} from "react-router-dom";
import useOrderMethodsStatus from "../../hooks/availability/useOrderMethodsStatus";
import {ORDER_METHODS} from "../../services/exports/Constants";
import HelperMethods from "../../services/exports/HelperMethods";
import collect from "collect.js";
import useOpeningStatus from "../../hooks/availability/useOpeningStatus";
import classNames from "classnames";

interface Props {
    openInfoModal: () => void;
}

export default function CompanyInfo(props: Props) {
    const { t } = useTranslation(null, { keyPrefix: 'Components:Company:CompanyInfo' });

    const { openInfoModal } = props;

    const { franchise, company } = useSelector((state: StoreInterface) => state.initialData);

    const navigate = useNavigate();
    const theme = useTheme();
    const { isDesktop } = useScreenType();
    const openingStatus = useOpeningStatus();
    const {
        visibleOrderMethods,
        isOrderMethodCurrentlyOpen,
        isOrderMethodOpenForFutureOrders,
        renderOrderMethodBasedCopy,
    } = useOrderMethodsStatus();

    const orderMethods = useMemo<ORDER_METHODS[]>(
        () => collect(visibleOrderMethods).take(2).toArray(),
        [visibleOrderMethods],
    );

    function renderAddress() {
        const address = company?.address + (company?.city ? `, ${company?.city}` : "");

        return <p className="text-mini text-brand-inkGrey-grey_4 mt-xmini">{address}</p>;
    }

    function renderOpeningHours() {
        return (
            <div className="flex mt-xmini items-center">
                <p className="text-mini text-brand-inkGrey-grey_4">{openingStatus.details_text}</p>
                <div className="flex ml-xxsmall items-center cursor-pointer" onClick={openInfoModal}>
                    <p className="text-mini text-brand-inkGrey-grey_4 mr-tiny font-semibold">{t("more_info")}</p>
                    <InfoIcon color={theme.color.brand.inkGrey.grey_4} />
                </div>
            </div>
        );
    }

    function renderAvailability() {
        const getTextColor = (isAvailable: boolean) =>
            isAvailable ? theme.color.text.light : theme.color.brand.inkGrey.grey_3;
        const getBackgroundColor = (isAvailable: boolean) =>
            isAvailable ? theme.color.companyBrand.primary : theme.color.background.inkDisabled.disabled_3;
        const getLabel = (isAvailable: boolean) => (isAvailable ? t("labels.available") : t("labels.unavailable"));

        return (
            <div className="flex lg:flex-row flex-col mt-xmini">
                {orderMethods.map((item, index) => {
                    const isAvailable = isOrderMethodCurrentlyOpen(item) || isOrderMethodOpenForFutureOrders(item);
                    const time = HelperMethods.formatTimeInterval(
                        item === ORDER_METHODS.DELIVERY
                            ? company?.average_order_delivery_time
                            : company?.average_order_preparation_time,
                    );

                    return (
                        <div
                            key={`order-method-${item}`}
                            className={classnames("flex flex-row items-center mr-small", {
                                "lg:mb-none mb-small": index === 0,
                            })}
                        >
                            <p className="mr-mini text-mini text-brand-inkGrey-grey_4">
                                {renderOrderMethodBasedCopy(item, t, "order_methods", { time })}:
                            </p>
                            <CustomLabel
                                label={getLabel(isAvailable)}
                                textColor={getTextColor(isAvailable)}
                                backgroundColor={getBackgroundColor(isAvailable)}
                            />
                        </div>
                    );
                })}
            </div>
        );
    }

    const renderImageCarousel = useMemo<JSX.Element>(() => {
        const pictureLength: number = company?.pictures?.length ?? 0;

        return (
            <div className="relative w-full lg:h-fit">
                <div className="relative lg:h-fit h-[150px]">
                    {pictureLength > 0 && (
                        <PicturesSlider
                            pictures={company?.pictures}
                            className={classNames("lg:rounded-xl rounded-none overflow-hidden lg:h-[270px]", {
                                "lg:h-[300px]": franchise?.has_multiple_companies,
                            })}
                        />
                    )}
                </div>
                {franchise?.logo_url && (
                    <div className="h-16 w-16 p-tiny bg-background-inkWhite-white_0 shadow-icon rounded-xl absolute left-[16px] bottom-0 translate-y-1/2 lg:h-20 lg:w-20">
                        <img className="h-full w-full object-contain" src={franchise?.logo_url} alt="logo" />
                    </div>
                )}
            </div>
        );
    }, [company?.pictures, franchise?.logo_url]);

    return (
        <div className="h-fit w-full">
            <div className="w-full relative flex flex-col justify-center">
                {renderImageCarousel}
                <div className={infoContainer}>
                    <h1 className="mr-small font-semibold" style={{ fontSize: theme.fonts.fontSizes.h3 }}>
                        {company?.name}
                    </h1>
                    {franchise?.has_multiple_companies && (
                        <div className="flex mt-xmini cursor-pointer" onClick={() => navigate("/", { replace: true })}>
                            <h6 className="text-mini my-auto mr-tiny text-brand-inkGrey-grey_4">
                                {t("buttons.change_store")}
                            </h6>
                            <BackIcon color={theme.color.brand.inkGrey.grey_4} className="rotate-180 h-3 w-3 my-auto" />
                        </div>
                    )}
                    {renderAddress()}
                    {renderOpeningHours()}
                    {renderAvailability()}
                </div>
            </div>
            {isDesktop && <div className="h-16 w-full" />}
        </div>
    );
}

const infoContainer =
    "lg:mt-none mt-large lg:ml-small py-small px-small lg:absolute relative justify-center flex flex-col z-10 border-b lg:border-none lg:shadow-section lg:mt-none lg:max-w-1/2 lg:w-fit lg:bg-background-inkWhite-white_1 lg:rounded-xl";
